/**
 * Dashboard widget — "Contracts awaiting customer signature"
 * Lists contract packs that have been sent but not yet signed by the customer.
 * Highlights anything older than 7 days and offers a one-click chase reminder.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { FileSignature, Loader2, ChevronRight, Bell, Clock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const ContractsAwaitingSignatureWidget = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/contract-packs/awaiting-signature');
      setRows(data || []);
    } catch (e) {
      // silent — widget hides if no data
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const sendReminder = async (customerId, packId) => {
    setReminding(packId);
    try {
      await api.post(`/customers/${customerId}/contract-pack/${packId}/remind`, {});
      toast.success('Reminder email sent');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reminder failed');
    } finally { setReminding(null); }
  };

  if (loading) {
    return (
      <Card data-testid="awaiting-sig-widget-loading">
        <CardContent className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></CardContent>
      </Card>
    );
  }
  if (!rows.length) return null;

  const overdue = rows.filter((r) => (r.days_since_sent ?? 0) >= 7);
  const recent = rows.filter((r) => (r.days_since_sent ?? 0) < 7);

  return (
    <Card data-testid="awaiting-signature-widget" className="border-amber-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileSignature className="w-5 h-5 text-amber-600" />
          Contracts Awaiting Customer Signature
          <Badge variant="outline" className="ml-2 border-amber-300 text-amber-700 text-[10px]">
            {rows.length} open
          </Badge>
          {overdue.length > 0 && (
            <Badge className="bg-rose-100 text-rose-700 border border-rose-200 text-[10px]">
              {overdue.length} overdue (&gt; 7 days)
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {[...overdue, ...recent].map((r) => {
          const isOverdue = (r.days_since_sent ?? 0) >= 7;
          return (
            <div
              key={r.pack_id}
              className={`flex flex-wrap items-center justify-between gap-2 p-2.5 rounded border ${
                isOverdue ? 'border-rose-200 bg-rose-50/40' : 'border-slate-100 hover:bg-slate-50'
              }`}
              data-testid={`awaiting-row-${r.pack_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/customers/${r.customer_id}?tab=contracts`}
                    className="font-medium text-sm text-slate-800 hover:text-blue-700"
                  >
                    {r.customer_name || 'Unnamed'}
                  </Link>
                  <Badge variant="outline" className="text-[10px]">{r.pack_type?.replace('_', ' ')}</Badge>
                  {isOverdue ? (
                    <Badge className="bg-rose-100 text-rose-700 border border-rose-200 text-[10px]">
                      <Clock className="w-2.5 h-2.5 mr-1" />
                      {r.days_since_sent}d overdue
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-200">
                      {r.days_since_sent ?? 0}d ago
                    </Badge>
                  )}
                  {r.reminder_count > 0 && (
                    <Badge variant="outline" className="text-[10px]">
                      Reminded {r.reminder_count}×
                    </Badge>
                  )}
                  {r.customer_first_viewed_at ? (
                    <Badge variant="outline" className="text-[10px] text-sky-700 border-sky-200 bg-sky-50" data-testid={`viewed-badge-${r.pack_id}`}>
                      <Eye className="w-2.5 h-2.5 mr-1" />
                      Opened {r.customer_view_count || 1}×
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-200" data-testid={`not-viewed-badge-${r.pack_id}`}>
                      <EyeOff className="w-2.5 h-2.5 mr-1" />
                      Not yet opened
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Sent {r.sent_at ? new Date(r.sent_at).toLocaleDateString('en-GB') : '—'}
                  {r.customer_email ? ` · ${r.customer_email}` : ''}
                </p>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant={isOverdue ? 'default' : 'outline'}
                  onClick={() => sendReminder(r.customer_id, r.pack_id)}
                  disabled={reminding === r.pack_id || !r.customer_email}
                  className={`h-8 text-xs ${isOverdue ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}`}
                  data-testid={`remind-${r.pack_id}`}
                >
                  {reminding === r.pack_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Bell className="w-3 h-3 mr-1" />}
                  {isOverdue ? 'Chase' : 'Remind'}
                </Button>
                <Link to={`/customers/${r.customer_id}?tab=contracts`}>
                  <Button size="sm" variant="ghost" className="h-8 text-xs" data-testid={`open-${r.pack_id}`}>
                    Open <ChevronRight className="w-3 h-3 ml-0.5" />
                  </Button>
                </Link>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default ContractsAwaitingSignatureWidget;
